import React, { useState, useEffect, useContext, useCallback, useRef } from "react";
import { withTranslation } from 'react-i18next';
import { TFunction, i18n } from 'i18next';
import { ITheme, IStyle } from 'office-ui-fabric-react/lib/Styling';
import { IStyleFunctionOrObject, customizable } from '@uifabric/utilities/lib/';
import { Spinner, SpinnerSize } from 'office-ui-fabric-react/lib/Spinner';
import { Stack } from 'office-ui-fabric-react/lib/Stack';
import { Text } from 'office-ui-fabric-react/lib/Text';
import { getUrlParameter, OnSendNotification, Scopes } from '../../ts/infrastructure';
import { getApmSafeState } from '../../ts/infrastructure/utils';
import { globalContext } from '../helpers/GlobalContexts';
import { Identity } from "../../ts/Ge/types";

interface UserOnBoardDialogState {
    isPolling: boolean;
    isSuccess: boolean;
    error?: string;
    identity?: Identity;
}

interface Props {
    t: TFunction;
    tReady: boolean;
    i18n: i18n;
    theme?: ITheme;
    styles?: IStyleFunctionOrObject<StyleProps, Styles>;
    pollingUrl?: string;
    pollingInterval?: number;
}

interface StyleProps {
    theme?: ITheme;
}

interface Styles {
    content: IStyle;
}

/**
 * This component is the user onboarding dialog that polls for user registration completion
 */
const UserOnBoardDialogComponent: React.FC<Props> = ({
    t,
    theme,
    // styles,
}) => {
    const context = useContext(globalContext);
    const UUID = getUrlParameter('uuid');
    const [state, setState] = useState<UserOnBoardDialogState>({
        isPolling: true,
        isSuccess: false,
        error: undefined,
        identity: undefined
    });
    const haveFallenBackOnce = useRef<boolean>(false);

    /**
     * Closes the dialog and sends notification to parent
     */
    const closeDialog = useCallback((success: boolean = false, error) => {
        const notification = {
            message: success ? state.identity : `User provision ${error ? 'failed: ' + error : 'cancelled'}`,
            success: success
        } as OnSendNotification;

        context.globalService.outlookActions.sendParentNotification(notification);
    }, [context]);

    // Start polling on component mount
    useEffect(() => {
        const parentTransactionId = getUrlParameter('transaction_id');
        const traceId = getUrlParameter('trace_id');
        const apm = context.globalService.apm;

        // Initialize a new transaction in the iframe connected to the parent transaction
        const transaction = apm.startTransaction('User provisioning', 'modal', { managed: false });

        if (transaction) {
            (transaction as any).traceId = traceId;
            transaction.addLabels({
                parentTransactionId,
                traceId
            });
            apm.setCustomContext({
                state: getApmSafeState(),
            });
        }

        const initializeOnboarding = async (userOnly: boolean = false) => {
            try {
                transaction?.startSpan('Initialize user onboarding', 'action');
                const outlookToken = await context.globalService.outlookActions.getNAAtoken(Scopes.GRAPH);
                const identity = await context.globalService.outlookActions.getUserInfo(outlookToken, userOnly);
                localStorage.setItem(`identity_${UUID}`, JSON.stringify(identity));
                transaction?.addLabels({
                    identity: JSON.stringify(identity),
                });
                setState(prevState => ({
                    ...prevState,
                    isPolling: false,
                    isSuccess: true,
                    identity: identity
                }));
            } catch (error) {
                const needToFallback = error?.message.includes('Could not find a potential provider account') || error?.message.includes('Could not link a provider account');

                if (!haveFallenBackOnce.current && needToFallback) {
                    haveFallenBackOnce.current = true; // run the fallback only once.
                    // report fallback
                    transaction?.addLabels({
                        user_only_fallback: true,
                        provision_error: error?.message
                    });
                    apm.setCustomContext({
                        user_only_fallback: true,
                        provision_error: error?.message
                    });
                    initializeOnboarding(true);
                } else {
                    setState(prevState => ({
                        ...prevState,
                        isPolling: false,
                        error: error?.message || 'Unknown error',
                    }));
                }

            } finally {
                if (transaction) {
                    transaction.end();
                }
            }
        };

        initializeOnboarding();
    }, [context]);

    useEffect(() => {
        if (state.isSuccess || state.error) {
            closeDialog(state.isSuccess, state.error);
        }

    }, [state.isSuccess, state.error, closeDialog]);
    
    const { isPolling } = state;

    return (
        <div
            id="dialog"
            className="ms-Grid"
            dir="ltr"
            style={{
                backgroundColor: theme?.palette.white || '#ffffff',
                display: 'flex',
                flexDirection: 'column',
                minHeight: '200px'
            }}
        >
            <div 
                className="ms-Grid-row" 
                style={{ 
                    color: theme?.palette.black || '#000000', 
                    flex: '1 0 0%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                <div id="portal-grid-wrapper" style={{
                        borderColor: theme.palette.neutralSecondary,
                        backgroundColor: theme.palette.white
                    }}>
                    <div id="portal-grid" className="provision">
                        <div className="heading-wrapper">
                            <img src={t("portal.icon_url")} alt="Panel secure image" width={window.innerWidth < 550 ? 25 : 60} />
                            <h2 className="heading-provision" style={{color: t("theme.portal_heading")}}>{t("portal.heading")}</h2>
                        </div>
                        <Stack 
                            verticalAlign="center" 
                            horizontalAlign="center" 
                            tokens={{ childrenGap: 20 }}
                            style={{ justifyContent: "space-around", height: '100%' }}
                        >
                            {isPolling && (
                                <>
                                    <Spinner 
                                        size={SpinnerSize.large} 
                                        style={{ 
                                            color: theme?.palette.themePrimary || '#0078d4',
                                        }} 
                                    />
                                    <Text 
                                        variant="medium" 
                                        style={{ 
                                            color: theme?.palette.black || '#000000',
                                            textAlign: 'center',
                                            fontWeight: '500'
                                        }}
                                    >
                                        {t("user_provision.provision_pending_primary")}
                                    </Text>
                                    <Text 
                                        variant="medium" 
                                        style={{ 
                                            color: theme?.palette.neutralSecondary || '#666666',
                                            textAlign: 'center'
                                        }}
                                    >
                                        {t("user_provision.provision_pending_secondary")}
                                    </Text>
                                </>
                            )}
                        </Stack>
                    </div>
                </div>
            </div>
        </div>
    );
};

export const UserOnBoardDialog = withTranslation()(customizable('UserOnBoardDialogComponent', ['theme', 'styles'], true)(UserOnBoardDialogComponent));